import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchDishesListThunk } from "../store/slices/dishesReducer";
import { getDishAverages } from "../utils/sustainabilityAverages";
import { useTranslation } from "../hooks/useTranslation";
import DishParameterCard from "../components/Cards/DishParameterCard.jsx";
import ProgressBarComponent from "../components/ProgressBarComponent.jsx";

const ReportsCenterPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dishesList } = useSelector((state) => state.dishes);
  const [selectedPeriod, setSelectedPeriod] = useState("30");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [scoreBandFilter, setScoreBandFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("score-desc");

  const dishes = useMemo(
    () => (Array.isArray(dishesList.data) ? dishesList.data : []),
    [dishesList.data]
  );

  const getDishCategoryId = (dish) =>
    String(dish?.affiliates_category_id ?? dish?.affiliate_category_id ?? "");

  useEffect(() => {
    const cacheAge = dishesList.lastFetched ? Date.now() - dishesList.lastFetched : null;
    const cacheValid = cacheAge && cacheAge < 120000;
    if (!dishesList.lastFetched || !cacheValid) {
      dispatch(fetchDishesListThunk());
    }
  }, [dispatch, dishesList.lastFetched]);

  const periodStart = useMemo(() => {
    const now = new Date();
    const days = Number(selectedPeriod);
    if (!Number.isFinite(days)) return null;
    const date = new Date(now);
    date.setDate(now.getDate() - days);
    return date;
  }, [selectedPeriod]);

  const categoryOptions = useMemo(() => {
    const map = new Map();
    dishes.forEach((dish) => {
      const id = getDishCategoryId(dish);
      const name = dish?.category?.name;
      if (id && name && !map.has(id)) {
        map.set(id, name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [dishes]);

  const filteredDishes = useMemo(() => {
    return dishes.filter((dish) => {
      if (selectedCategory !== "all" && getDishCategoryId(dish) !== selectedCategory) {
        return false;
      }
      if (!periodStart) return true;
      const updated = dish?.updated_at ? new Date(dish.updated_at) : null;
      if (!updated || Number.isNaN(updated.getTime())) return false;
      return updated >= periodStart;
    });
  }, [dishes, selectedCategory, periodStart]);

  const ranked = useMemo(() => {
    const list = filteredDishes.map((dish) => {
      const avg = getDishAverages(dish);
      const total = Math.round((avg.environment_average + avg.health_average + avg.social_average) / 3);
      return {
        id: dish.id,
        name: dish.name,
        score: total,
        environment: avg.environment_average,
        health: avg.health_average,
        social: avg.social_average,
        updated_at: dish.updated_at,
      };
    });

    return list.sort((a, b) => b.score - a.score);
  }, [filteredDishes]);

  const summary = useMemo(() => {
    if (ranked.length === 0) {
      return {
        recipes: 0,
        avg: 0,
        top: null,
        low: null,
      };
    }

    const avg = Math.round(ranked.reduce((acc, item) => acc + item.score, 0) / ranked.length);
    return {
      recipes: ranked.length,
      avg,
      top: ranked[0],
      low: ranked[ranked.length - 1],
    };
  }, [ranked]);

  const scoreDistribution = useMemo(() => {
    const buckets = {
      excellent: 0, // 80-100
      good: 0, // 60-79
      fair: 0, // 40-59
      low: 0, // 0-39
    };

    ranked.forEach((item) => {
      if (item.score >= 80) buckets.excellent += 1;
      else if (item.score >= 60) buckets.good += 1;
      else if (item.score >= 40) buckets.fair += 1;
      else buckets.low += 1;
    });

    return buckets;
  }, [ranked]);

  const bandFilteredRanked = useMemo(() => {
    if (scoreBandFilter === "all") return ranked;
    return ranked.filter((item) => {
      if (scoreBandFilter === "excellent") return item.score >= 80;
      if (scoreBandFilter === "good") return item.score >= 60 && item.score < 80;
      if (scoreBandFilter === "fair") return item.score >= 40 && item.score < 60;
      if (scoreBandFilter === "low") return item.score < 40;
      return true;
    });
  }, [ranked, scoreBandFilter]);

  const tableRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const searched = bandFilteredRanked.filter((item) =>
      !query ? true : String(item.name || "").toLowerCase().includes(query)
    );

    const rows = [...searched];
    rows.sort((a, b) => {
      if (sortBy === "score-asc") return a.score - b.score;
      if (sortBy === "name-asc") return String(a.name || "").localeCompare(String(b.name || ""));
      if (sortBy === "name-desc") return String(b.name || "").localeCompare(String(a.name || ""));
      return b.score - a.score;
    });

    return rows;
  }, [bandFilteredRanked, searchQuery, sortBy]);

  const rowLimit = 30;

  const getScoreBadgeClass = (score) => {
    if (score >= 80) return "bg-emerald-100 text-emerald-700";
    if (score >= 60) return "bg-blue-100 text-blue-700";
    if (score >= 40) return "bg-amber-100 text-amber-700";
    return "bg-rose-100 text-rose-700";
  };

  const formatDate = (value) => {
    const parsed = value ? new Date(value) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleDateString("sv-SE");
  };

  const dashboardInsights = useMemo(() => {
    const withMissingImage = dishes.filter((dish) => !dish?.image).length;
    const pendingSync = dishes.filter((dish) => dish?.has_product_sync_notice).length;
    const lowSustainability = dishes.filter((dish) => {
      const avg = getDishAverages(dish);
      const total = Math.round((avg.environment_average + avg.health_average + avg.social_average) / 3);
      return total < 50;
    }).length;

    return {
      withMissingImage,
      pendingSync,
      lowSustainability,
    };
  }, [dishes]);

  const handleExportCsv = () => {
    const header = ["Recipe", "Score", "Environment", "Health", "Social", "Updated"];
    const rows = ranked.map((r) => [
      r.name,
      String(r.score),
      String(r.environment),
      String(r.health),
      String(r.social),
      r.updated_at || "",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `affiliate-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t("Hållbarhetsöversikt")}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {t("Analysera receptens hållbarhet, följ trender och exportera rapportunderlag.")}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="flex flex-col laptop:flex-row laptop:items-end laptop:justify-between gap-4">
            <div className="flex flex-col mobile:flex-row gap-3">
              <div className="w-full mobile:w-[220px]">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t("Period")}</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="7">{t("Senaste 7 dagar")}</option>
                  <option value="30">{t("Senaste 30 dagar")}</option>
                  <option value="90">{t("Senaste 90 dagar")}</option>
                  <option value="365">{t("Senaste 12 månader")}</option>
                </select>
              </div>

              <div className="w-full mobile:w-[260px]">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t("Kategori")}</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="all">{t("Alla kategorier")}</option>
                  {categoryOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col mobile:flex-row gap-3 laptop:justify-end">
              <button
                type="button"
                onClick={handleExportCsv}
                className="w-full mobile:w-auto px-5 bg-secondary-green hover:bg-secondary-green/90 text-white text-sm font-semibold py-2 rounded-lg"
              >
                {t("Exportera CSV")}
              </button>
              <Link
                to="/dashboard"
                className="w-full mobile:w-auto px-5 text-center bg-gray-100 border-none hover:bg-gray-50 text-gray-700 text-sm font-semibold py-2 rounded-lg"
              >
                {t("Öppna Dashboard")}
              </Link>
              <Link
                to="/menyer"
                className="w-full mobile:w-auto px-5 text-center bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold py-2 rounded-lg"
              >
                {t("Öppna Menyer")}
              </Link>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {[
              { value: "7", label: t("7d") },
              { value: "30", label: t("30d") },
              { value: "90", label: t("90d") },
              { value: "365", label: t("12m") },
            ].map((period) => (
              <button
                key={period.value}
                type="button"
                onClick={() => setSelectedPeriod(period.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                  selectedPeriod === period.value
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 mobile:grid-cols-2 laptop:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
            <p className="text-xs text-gray-500">{t("Recept i urval")}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{tableRows.length}</p>
            <p className="text-xs text-gray-500 mt-1">{t("Efter filter och sökning")}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
            <p className="text-xs text-gray-500">{t("Genomsnittlig score")}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.avg}</p>
            <p className="text-xs text-gray-500 mt-1">{t("För valt urval")}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
            <p className="text-xs text-gray-500">{t("Bästa recept")}</p>
            <p className="text-sm font-semibold text-gray-900 mt-1 truncate">{summary.top?.name || "-"}</p>
            <p className="text-xs text-gray-500">{summary.top ? `${summary.top.score}` : ""}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
            <p className="text-xs text-gray-500">{t("Lägsta recept")}</p>
            <p className="text-sm font-semibold text-gray-900 mt-1 truncate">{summary.low?.name || "-"}</p>
            <p className="text-xs text-gray-500">{summary.low ? `${summary.low.score}` : ""}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="mb-5">
            <h2 className="text-lg font-bold text-gray-900">{t("Grafisk sammanfattning")}</h2>
            <p className="text-sm text-gray-500 mt-1">
              {t("Snabb visuell överblick av resultat och fördelning.")}
            </p>
          </div>

          <div className="grid grid-cols-1 laptop:grid-cols-2 gap-6 items-stretch">
            <div className="rounded-xl border border-gray-100 p-4 bg-gray-50/60 h-full flex flex-col">
              <p className="text-sm font-semibold text-gray-800 mb-4">{t("Genomsnitt per område")}</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                {
                  key: "env",
                  label: "Miljö",
                  iconSrc: "/assets/icons/svg/Miljo.svg",
                  value: Math.round(ranked.reduce((acc, item) => acc + item.environment, 0) / (ranked.length || 1)),
                },
                {
                  key: "health",
                  label: "Hälsa",
                  iconSrc: "/assets/icons/svg/Halsa.svg",
                  value: Math.round(ranked.reduce((acc, item) => acc + item.health, 0) / (ranked.length || 1)),
                },
                {
                  key: "social",
                  label: "Rättvisa",
                  iconSrc: "/assets/icons/svg/Rattvisa.svg",
                  value: Math.round(ranked.reduce((acc, item) => acc + item.social, 0) / (ranked.length || 1)),
                },
                { key: "total", label: "Genomsnitt", iconSrc: "/assets/icons/svg/Visa.svg", value: summary.avg },
              ].map((metric) => (
                <DishParameterCard
                    key={metric.key}
                    title={metric.label}
                    iconSrc={metric.iconSrc}
                    score={Math.max(0, Math.min(100, metric.value))}
                    disabled={false}
                    t={t}
                  />
              ))}
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 p-4 bg-gray-50/60 h-full flex flex-col">
              <p className="text-sm font-semibold text-gray-800 mb-4">{t("Fördelning av receptscore")}</p>
              <div className="flex-1 flex flex-col gap-0">
              {[
                {
                  key: "excellent",
                  label: t("Utmärkt (80-100)"),
                  count: scoreDistribution.excellent,
                },
                {
                  key: "good",
                  label: t("Bra (60-79)"),
                  count: scoreDistribution.good,
                },
                {
                  key: "fair",
                  label: t("Acceptabel (40-59)"),
                  count: scoreDistribution.fair,
                },
                {
                  key: "low",
                  label: t("Låg (0-39)"),
                  count: scoreDistribution.low,
                },
              ].map((bucket) => {
                const percentage = summary.recipes > 0 ? Math.round((bucket.count / summary.recipes) * 100) : 0;
                return (
                  <button
                    type="button"
                    key={bucket.key}
                    onClick={() => setScoreBandFilter((prev) => (prev === bucket.key ? "all" : bucket.key))}
                    className={`w-full text-left rounded-lg p-2 transition ${
                      scoreBandFilter === bucket.key ? "bg-gray-100" : "hover:bg-gray-100/70"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-700">{bucket.label}</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {bucket.count} ({percentage})
                      </span>
                    </div>
                    <ProgressBarComponent
                      name={bucket.label}
                      score={percentage}
                      isIcon={false}
                      t={t}
                    />
                  </button>
                );
              })}
              </div>
              <button
                type="button"
                onClick={() => setScoreBandFilter("all")}
                className={`mt-auto pt-2 text-xs font-semibold px-3 py-1.5 rounded-full border ${
                  scoreBandFilter === "all"
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                }`}
              >
                {t("Visa alla nivåer")}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
          <div className="p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 border-b border-gray-100">
            <h2 className="text-xl mobile:text-2xl tablet:text-2xl laptop:text-2xl desktop:text-2xl font-bold text-gray-900">
              {t("Snabbåtgärder")}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t("Gå direkt till de recept som behöver fokus just nu.")}
            </p>
          </div>
          <div className="p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-3 desktop:grid-cols-3 gap-4">
            <Link
              to="/menyer?view=missing-image"
              className="rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors p-4"
            >
              <p className="text-xs text-gray-500">{t("Saknar bild")}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{dashboardInsights.withMissingImage}</p>
            </Link>
            <Link
              to="/menyer?view=needs-sync"
              className="rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors p-4"
            >
              <p className="text-xs text-gray-500">{t("Har synknotis")}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{dashboardInsights.pendingSync}</p>
            </Link>
            <Link
              to="/menyer?view=low-sustainability"
              className="rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors p-4"
            >
              <p className="text-xs text-gray-500">{t("Låg hållbarhet (<50)")}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{dashboardInsights.lowSustainability}</p>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-3">
              <h2 className="text-lg font-bold text-gray-900">{t("Receptöversikt")}</h2>
              <div className="flex flex-col mobile:flex-row gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("Sök recept...")}
                  className="w-full mobile:w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full mobile:w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="score-desc">{t("Sortera: Högst score")}</option>
                  <option value="score-asc">{t("Sortera: Lägst score")}</option>
                  <option value="name-asc">{t("Sortera: Namn A-Ö")}</option>
                  <option value="name-desc">{t("Sortera: Namn Ö-A")}</option>
                </select>
              </div>
            </div>
          </div>
          {tableRows.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">{t("Inga recept matchar filtret.")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">#</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">{t("Recept")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">{t("Total")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">{t("Miljö")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">{t("Hälsa")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">{t("Rättvisa")}</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-6 py-3">{t("Uppdaterad")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tableRows.slice(0, rowLimit).map((dish, index) => (
                    <tr key={dish.id} className={`hover:bg-gray-50 ${index % 2 !== 0 ? "bg-gray-50/50" : ""}`}>
                      <td className="px-6 py-3 text-sm font-semibold text-gray-500">{index + 1}</td>
                      <td className="px-6 py-3 text-sm text-gray-900 max-w-[260px] truncate" title={dish.name}>
                        {dish.name}
                      </td>
                      <td className="px-6 py-3 text-sm font-semibold text-gray-900">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${getScoreBadgeClass(dish.score)}`}>
                          {dish.score}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700">{dish.environment}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{dish.health}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">{dish.social}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{formatDate(dish.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tableRows.length > rowLimit && (
            <div className="px-6 py-3 border-t border-gray-100 text-xs text-gray-500">
              {t("Visar")} {rowLimit} / {tableRows.length} {t("recept")} ({t("förfina filter eller sökning för mer precision")}).
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsCenterPage;
